"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Card } from "@/components/ui/card";
import { PenTool, Monitor, Share2, ChartBar, Search, Target, ShoppingCart, Settings, Camera, ShieldCheck, CalendarDays, Mail, Zap } from "lucide-react";
const getIconForService = (title: string) => {
  const normalizedTitle = title.toLowerCase();
  if (normalizedTitle.includes("branding")) return PenTool;
  if (normalizedTitle.includes("website") || normalizedTitle.includes("web")) return Monitor;
  if (normalizedTitle.includes("redes sociais") || normalizedTitle.includes("social")) return Share2;
  if (normalizedTitle.includes("dados") || normalizedTitle.includes("data")) return ChartBar;
  if (normalizedTitle.includes("google")) return Search;
  if (normalizedTitle.includes("ads") || normalizedTitle.includes("publicidade")) return Target;
  if (normalizedTitle.includes("marketplace")) return ShoppingCart;
  if (normalizedTitle.includes("software") || normalizedTitle.includes("sistema")) return Settings;
  if (normalizedTitle.includes("foto") || normalizedTitle.includes("vídeo") || normalizedTitle.includes("video")) return Camera;
  if (normalizedTitle.includes("cibersegurança") || normalizedTitle.includes("segurança")) return ShieldCheck;
  if (normalizedTitle.includes("feira") || normalizedTitle.includes("evento")) return CalendarDays;
  if (normalizedTitle.includes("email") || normalizedTitle.includes("e-mail")) return Mail;
  return Zap;
};
function ServicesGridConsumer() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div data-section-id="225894" className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        {[...Array(8)].map((_, i) => <div key={i} data-index={i} className="h-[280px] rounded-sm bg-muted/50 animate-pulse border border-border/50" />)}
      </div>;
  }
  if (error) {
    return <div className="text-center py-12 text-muted-foreground">
        <p>Unable to load our specialist team profiles.</p>
      </div>;
  }
  return <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
      {posts.map((post, i) => {
      const title = post.customFields?.titulo || post.title;
      const description = post.customFields?.descricao || "Specialist professional committed to delivering high-quality construction solutions and compliance.";
      const Icon = getIconForService(title);
      const labelNumber = (i + 1).toString().padStart(2, '0');
      const serviceImage = {
        src: post.customFields?.image || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1641543/img/service_placeholder.webp",
        srcset: "",
        sizes: ""
      };
      return <Link key={post.id} to={post.link || "#"} data-index={i} className="group block h-full outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-sm">
            <Card className="h-full bg-card border border-border rounded-sm p-6 md:p-8 flex flex-col gap-6 transition-all duration-300 hover:border-primary hover:shadow-md hover:-translate-y-1">
              <div className="aspect-square overflow-hidden bg-muted -mx-6 md:-mx-8 -mt-6 md:-mt-8 mb-2">
                <img src={serviceImage.src} data-wvc-srcset={serviceImage.srcset} data-wvc-sizes={serviceImage.sizes} alt={title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
              </div>
              
              <div className="flex flex-col flex-grow">
                <div className="inline-block w-fit px-2 py-0.5 bg-muted text-[10px] font-mono text-muted-foreground uppercase mb-4 border border-border">
                  Specialist
                </div>
                <h3 className="text-lg font-black text-foreground font-default uppercase mb-3 tracking-tight">{title}</h3>
                <p className="text-sm text-muted-foreground font-sans leading-relaxed line-clamp-3 mb-6">{description}</p>
                
                <div className="mt-auto pt-6 border-t border-border/50 flex justify-between items-center">
                  <span className="text-[10px] font-mono text-primary uppercase tracking-widest">REF.{labelNumber}</span>
                  <Icon className="w-5 h-5 text-foreground stroke-[1.5] transition-transform duration-300 group-hover:scale-110" />
                </div>
              </div>
            </Card>
          </Link>;
    })}
    </div>;
}
export default function PremiumServices() {
  const wp_query_params = {
    post_type: "service",
    per_page: 12,
    orderby: "title",
    order: "asc"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="ourteam" className="bg-background py-[120px] text-foreground">
      <div className="container mx-auto px-4 md:px-6">
        <div className="max-w-3xl mb-16">
          <h2 className="text-4xl md:text-6xl font-black font-default uppercase tracking-tighter mb-6 text-foreground">
            OUR TEAM
          </h2>
          <div className="w-20 h-1 bg-primary mb-8" />
          <p className="text-lg text-muted-foreground font-sans leading-relaxed max-w-2xl">
            Our experienced team at Bottom Up Projects works collaboratively with clients to ensure projects are completed safely, efficiently, and on time. We combine industrial precision with a strong focus on quality, compliance, and reliability across all commercial fitouts.
          </p>
          <h3 className="text-2xl font-black font-default uppercase tracking-tighter mt-10 mb-4 text-foreground">
            STAFF
          </h3>
          <p className="text-lg text-muted-foreground font-sans leading-relaxed max-w-2xl">
            Our internal team of project managers, site supervisors, and administrative professionals are dedicated to delivering excellence through disciplined coordination and technical expertise.
          </p>
          <h3 className="text-2xl font-black font-default uppercase tracking-tighter mt-10 mb-4 text-foreground">
            OUR CONSULTANTS
          </h3>
          <p className="text-lg text-muted-foreground font-sans leading-relaxed max-w-2xl">
            We partner with industry-leading consultants, including architects and engineers, to ensure every project meets rigorous technical standards and regulatory requirements.
          </p>
          <h3 className="text-2xl font-black font-default uppercase tracking-tighter mt-10 mb-4 text-foreground">
            OUR SUBCONTRACTORS
          </h3>
          <p className="text-lg text-muted-foreground font-sans leading-relaxed max-w-2xl">
            Our vetted subcontractors are selected for their expertise and commitment to safety, ensuring high-quality workmanship and reliable delivery across all project phases.
          </p>
        </div>
        <PostsProvider wp_query={{
        post_type: "service",
        per_page: 12,
        orderby: "title",
        order: "asc"
      }}>
          <ServicesGridConsumer />
        </PostsProvider>
      </div>
    </section>;
}